import { Injectable, OnDestroy, inject } from '@angular/core'

import { BehaviorSubject, Observable, map } from 'rxjs'
import { tap } from 'rxjs/operators'

import { PaginationQueryParams } from '@flowx/designer/shared/api/api-http.service'
import { RuntimeApiService } from '@flowx/designer/shared/api/runtime.api'
import {
  ExceptionTypeMappings,
  ProcessException,
  ProcessExceptionsQueryParams,
} from '@flowx/designer/shared/entities/process-exceptions.types'
import { ApplicationDetails } from '@flowx/designer/shared/entities/process-instance.types'
import { GridSearchService } from '@flowx/designer/shared/services/grid-search.service'
import {
  PaginatedEndpoint,
  PaginatedResponse,
  Sort,
} from '@flowx/designer/shared/services/paginated-datasource'

export interface ApplicationProcessException {
  exception: ProcessException
  applicationDetails: ApplicationDetails
}

const SORT_FIELD = 'date'

@Injectable({
  providedIn: 'root',
})
export class ProcessExceptionsService
  extends GridSearchService<ProcessException>
  implements OnDestroy
{
  #runtimeService = inject(RuntimeApiService)
  private _processException$ = new BehaviorSubject<ProcessException>(null)
  private _applicationDetails$ = new BehaviorSubject<ApplicationDetails>(null)
  private _processExceptionLoading$ = new BehaviorSubject<boolean>(false)
  private initialQuery: Partial<ProcessExceptionsQueryParams> = {}

  getGridEndpointInfo(): {
    endpoint: PaginatedEndpoint<any, ProcessException>
    sort: Sort
    query: Partial<any>
  } {
    return {
      endpoint: this.getExceptions.bind(this),
      sort: {
        property: SORT_FIELD,
        direction: 'desc',
      },
      query: this.initialQuery,
    }
  }

  getExceptions(
    params: PaginationQueryParams,
    query: Partial<any>
  ): Observable<PaginatedResponse<ProcessException>> {
    return this.#runtimeService.getExceptions({ ...params, ...query }).pipe(
      map((response) => ({
        ...response,
        content: response.content.map((exception) => this.transformException(exception)),
      }))
    )
  }
  getException(id: number): void {
    this._processExceptionLoading$.next(true)
    this.#runtimeService
      .getExceptionById(id)
      .pipe(
        map((response): ApplicationProcessException => ({
          exception: response.resourceContent,
          applicationDetails: response.applicationDetails,
        })),
        tap((applicationDetailsException: ApplicationProcessException) => {
          this._processException$.next(
            this.transformException(applicationDetailsException.exception)
          )
          this._applicationDetails$.next(applicationDetailsException.applicationDetails)
          this._processExceptionLoading$.next(false)
        })
      )
      .subscribe()
  }

  transformException(exception: ProcessException): ProcessException {
    return {
      ...exception,
      formattedSource: `${exception.source} ${
        exception.causeName ? ` (${exception.causeName})` : ''
      }`,
      formattedType: ExceptionTypeMappings[exception.type],
    }
  }

  get processException$(): Observable<ProcessException> {
    return this._processException$.asObservable()
  }
  get applicationDetails$(): Observable<ApplicationDetails> {
    return this._applicationDetails$.asObservable()
  }

  get processExceptionLoading$(): Observable<boolean> {
    return this._processExceptionLoading$.asObservable()
  }

  ngOnDestroy(): void {
    this.reset()
  }
}
